/* ========================
 * SnippetsDrawer.jsx - UI for displaying and managing snippets
 * Simple text-based search and filtering only
 * Uses ReactMarkdown for rendering snippet content with theme-aware styling.
 * ======================== */
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Tag, X, Plus, Copy, Check, Edit2, Trash2 } from 'lucide-react';
import { useSnippets } from '@/managers/SnippetsManager';
import PropTypes from 'prop-types';
import AddSnippetModal from './AddSnippetModal';
import ConfirmSnippetDeleteModal from './ConfirmSnippetDeleteModal';
import { HousePortal } from '@/App'; 
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/* ========================
 * SnippetSkeletonItem - A placeholder item for the snippet loading state
 * ======================== */
const SnippetSkeletonItem = () => {
  const baseBg = 'bg-surface'; 
  const pulseBgInner = 'bg-surface-dark'; 

  return (
    <div className={`px-3 py-2.5 ${baseBg} animate-pulse border-b border-border-color`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1 pr-2 space-y-2">
          <div className={`h-4 rounded ${pulseBgInner} w-3/5`}></div>
          <div className={`h-3 rounded ${pulseBgInner} w-1/3`}></div>
          <div className="flex flex-wrap gap-1">
            <div className={`h-3 w-10 rounded ${pulseBgInner}`}></div>
            <div className={`h-3 w-12 rounded ${pulseBgInner}`}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SnippetsDrawer = ({ isOpen, onClose }) => {
  const [selectedSnippetDetail, setSelectedSnippetDetail] = useState(null);
  const [snippetBeingEdited, setSnippetBeingEdited] = useState(null);
  const [snippetPendingDelete, setSnippetPendingDelete] = useState(null);
  const [copySuccessStates, setCopySuccessStates] = useState({});

  const {
    snippets, 
    deleteSnippet,
    getAllTags,
    searchQuery,
    setSearchQuery,
    selectedTags,
    setSelectedTags,
    loadSnippets,
    unloadSnippets,
    isLoading,
    isSnippetsLoaded
  } = useSnippets();

  useEffect(() => {
    if (isOpen) {
      loadSnippets();
    } else {
      const timer = setTimeout(() => {
         unloadSnippets();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, loadSnippets, unloadSnippets]);

  const formatDate = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'Invalid Date';
    try {
      return (
        date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
        }) +
        ' \u00B7 ' +
        date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    } catch (e) {
      console.error("Error formatting date:", e);
      return 'Invalid Date';
    }
  };

  const handleTagClick = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      }
      return [...prev, tag];
    });
  };

  const handleCopyContent = async (snippetId, content) => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccessStates(prev => ({ ...prev, [snippetId]: true }));
      setTimeout(() => setCopySuccessStates(prev => ({ ...prev, [snippetId]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy content:', err);
    }
  };
  
  const SnippetDetailModal = ({ snippet, onCloseDetail, onEdit, onDelete }) => {
    if (!snippet) return null;
    const currentCopySuccess = copySuccessStates[snippet.id] || false;

    const componentsForMarkdown = {
      p: ({node, ...props}) => <p className="text-[14px] my-1 leading-relaxed whitespace-pre-wrap text-on-surface" {...props} />,
      code({node, inline, className, children, ...props}) {
        const match = /language-(\w+)/.exec(className || '');
        if (inline) {
          return (
            <code
              className="px-1 py-0.5 text-sm rounded bg-black/30 text-amber-400 font-mono"
              {...props}
            >
              {children}
            </code>
          );
        }
        return (
          <pre className="my-2 p-3 rounded text-sm bg-black/30 styled-scrollbar-thin overflow-x-auto">
            <code className={`${className || ''} text-gray-100 font-mono`} {...props}>
              {String(children).replace(/\n$/, '')}
            </code>
          </pre>
        );
      },
      ul: ({node, ordered, ...props}) => <ul className="list-disc list-inside my-2 pl-4 text-on-surface" {...props} />,
      ol: ({node, ordered, ...props}) => <ol className="list-decimal list-inside my-2 pl-4 text-on-surface" {...props} />,
      li: ({node, ...props}) => <li className="mb-1" {...props} />,
      blockquote: ({node, ...props}) => <blockquote className="my-2 pl-4 border-l-4 border-gray-700 bg-black/20 text-on-surface-dim p-2 italic" {...props} />,
      a: ({node, ...props}) => <a className="underline text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer" {...props} />,
      hr: ({node, ...props}) => <hr className="my-4 border-white/10" {...props} />,
      h1: ({node, ...props}) => <h1 className="text-2xl font-bold my-3 text-on-surface" {...props} />,
      h2: ({node, ...props}) => <h2 className="text-xl font-semibold my-2.5 text-on-surface" {...props} />,
      h3: ({node, ...props}) => <h3 className="text-lg font-semibold my-2 text-on-surface" {...props} />,
    };

    return (
      <HousePortal>
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={onCloseDetail}
        >
          <div
            className="rounded-xl max-w-3xl w-full m-auto shadow-xl flex flex-col bg-surface border border-border-color"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border-color">
              <div className="space-y-0.5">
                <h3 className="text-lg font-semibold text-on-surface">
                  {snippet.name}
                </h3>
                <div className="flex items-center gap-2 text-xs text-on-surface-dim">
                  <span>{formatDate(snippet.updatedAt)}</span>
                  {snippet.language && <span className="capitalize">&middot; {snippet.language}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onDelete(snippet.id)}
                  title="Delete Snippet"
                  className="p-2 rounded-lg transition-colors hover:bg-red-500/20 hover:text-error text-on-surface-dim"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={onEdit}
                  title="Edit Snippet"
                  className="p-2 rounded-lg transition-colors hover:bg-white/10 text-on-surface-dim"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleCopyContent(snippet.id, snippet.code)}
                  title="Copy Content"
                  className="p-2 rounded-lg transition-colors flex items-center gap-1.5 hover:bg-white/10 text-on-surface-dim"
                >
                  {currentCopySuccess ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
                <button
                  onClick={onCloseDetail}
                  title="Close Detail View"
                  className="p-2 rounded-lg transition-colors hover:bg-white/10 text-on-surface-dim"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-5 max-h-[70vh] overflow-y-auto styled-scrollbar">
              {(typeof snippet.code !== 'string' || !snippet.code.trim()) ? (
                <div className="text-sm text-on-surface-dim">No content.</div>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={componentsForMarkdown}
                >
                  {snippet.code}
                </ReactMarkdown>
              )}

              {snippet.notes && (
                <div className="mt-6 pt-4 border-t border-border-color">
                  <h4 className="text-xs font-semibold uppercase tracking-wider mb-2 text-on-surface-dim">Notes</h4>
                  <p className="text-sm whitespace-pre-wrap text-on-surface-dim">
                    {snippet.notes}
                  </p>
                </div>
              )}
               {snippet.tags && snippet.tags.length > 0 && (
                <div className="mt-6 pt-4 border-t border-border-color">
                  <h4 className="text-xs font-semibold uppercase tracking-wider mb-2 text-on-surface-dim">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {snippet.tags.map(tag => (
                       <span key={tag} className="px-2 py-0.5 rounded text-xs bg-white/10 text-on-surface-dim">
                         {tag}
                       </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </HousePortal>
    );
  };

  const handleDeleteConfirmation = async () => {
    if (!snippetPendingDelete) return;

    try {
      await deleteSnippet(snippetPendingDelete.id);
      if (selectedSnippetDetail?.id === snippetPendingDelete.id) {
        setSelectedSnippetDetail(null);
      }
      setSnippetPendingDelete(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const hasActiveFilters = searchQuery || selectedTags.length > 0;
  let emptyStateMessage = "You haven't saved any snippets yet. Click the '+' icon to add one!";
  if ((isSnippetsLoaded || !isLoading) && snippets.length === 0) { 
    if (hasActiveFilters) {
      emptyStateMessage = "No snippets match your current filters.";
    }
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
            <motion.div
                initial={{ opacity: 0, x: '100%' }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: '100%' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              className="h-full bg-surface flex flex-col w-full"
              style={{ backgroundColor: 'var(--surface)', opacity: 1 }}
            >
                {/* Header */}
                <div className="flex items-center h-[50px] justify-between px-4 shrink-0 border-b border-border-color bg-surface">
                  <h2 className="text-base font-semibold text-on-surface">Snippets</h2>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setSnippetBeingEdited({})}
                      className="p-2 rounded-lg transition-colors hover:bg-surface-dark text-on-surface-dim"
                      aria-label="Add new snippet"
                      title="Add new snippet"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-lg transition-colors hover:bg-surface-dark text-on-surface-dim"
                      aria-label="Close snippets panel"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Search and Tags */}
                <div className="p-3 space-y-3 shrink-0 border-b border-border-color">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search name, content, tags..."
                      className="w-full rounded-lg pl-9 pr-3 py-2 text-sm transition-colors
                        focus:outline-none focus:ring-2 focus:ring-primary/30
                        bg-surface border border-border-color text-on-surface placeholder:text-on-surface-dim focus:border-primary"
                    />
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-dim"
                    />
                  </div>

                  {getAllTags()?.length > 0 && ( 
                    <div className="flex flex-wrap gap-1.5">
                      {getAllTags().map((tag) => (
                        <button
                          key={tag}
                          onClick={() => handleTagClick(tag)}
                          className={`
                            px-2.5 py-1 rounded-full text-xs flex items-center gap-1 transition-all duration-200 group
                            ${selectedTags.includes(tag)
                              ? 'bg-primary text-white shadow-md'
                              : 'bg-surface text-on-surface-dim hover:bg-surface-dark'
                            }
                          `}
                        >
                          <Tag size={10} className={`${selectedTags.includes(tag) ? 'opacity-70' : ''}`} />
                          <span className="truncate max-w-[100px]">{tag}</span>
                           {selectedTags.includes(tag) && <X size={10} className="ml-0.5 opacity-70 group-hover:opacity-100"/>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Snippets List */}
                <div className="flex-1 overflow-y-auto styled-scrollbar">
                  {isLoading && !isSnippetsLoaded && ( 
                    <div className="space-y-px"> 
                      <SnippetSkeletonItem />
                      <SnippetSkeletonItem />
                      <SnippetSkeletonItem />
                      <SnippetSkeletonItem />
                    </div>
                  )}
                  {!isLoading && snippets.length === 0 && (
                    <div className="p-6 text-center text-sm text-on-surface-dim">
                      {emptyStateMessage}
                    </div>
                  )}
                  {isSnippetsLoaded && !isLoading && snippets.length > 0 && (
                    snippets.map((snippet) => {
                      return (
                        <div
                          key={snippet.id}
                          onClick={() => setSelectedSnippetDetail(snippet)}
                          className="block px-3 py-2.5 cursor-pointer group transition-all duration-200 hover:bg-surface border-b border-border-color"
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1 pr-2">
                              <div className="text-sm font-medium mb-0.5 truncate text-on-surface">
                                {snippet.name}
                              </div>
                              <div className="text-xs mb-1.5 text-on-surface-dim">
                                {formatDate(snippet.updatedAt)}
                              </div>
                              {snippet.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {snippet.tags.slice(0, 3).map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-1.5 py-0.5 rounded text-[10px] bg-surface-dark text-on-surface-dim"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {snippet.tags.length > 3 && (
                                     <span className="px-1.5 py-0.5 rounded text-[10px] bg-surface-dark text-on-surface-dim">
                                      +{snippet.tags.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      {selectedSnippetDetail && (
          <SnippetDetailModal
            snippet={selectedSnippetDetail}
            onCloseDetail={() => setSelectedSnippetDetail(null)}
            onEdit={() => {
              setSnippetBeingEdited(selectedSnippetDetail);
              setSelectedSnippetDetail(null);
            }}
            onDelete={() => setSnippetPendingDelete(selectedSnippetDetail)}
          />
      )}

      <ConfirmSnippetDeleteModal
        isOpen={Boolean(snippetPendingDelete)}
        onClose={() => setSnippetPendingDelete(null)}
        onConfirm={handleDeleteConfirmation}
        snippetName={snippetPendingDelete?.name}
      />

      {/* Add/Edit Modal */}
      {snippetBeingEdited !== null && (
        <AddSnippetModal
          isOpen={true}
          onClose={() => setSnippetBeingEdited(null)}
          existingSnippet={
            snippetBeingEdited && Object.keys(snippetBeingEdited).length > 0 ? snippetBeingEdited : null
          }
        />
      )}
    </>
  );
};

SnippetsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SnippetsDrawer;